import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom, Subject, Subscription, takeUntil } from 'rxjs';
import {
  IFeatureType,
  IOrderInfo,
  ISubscriptionPlanInfo,
  ISubscriptionPlanWithTaxAndDiscountInfo,
  OrderStatus,
  PaymentStatus,
} from '../../../models/Subscription';
import { Actions, ofType } from '@ngrx/effects';
import {
  FetchSubscriptionPlansAction,
  FulFillOrderAction,
  FulfillOrderSuccessAction,
  GeneratePaymentOrderAction,
  GeneratePaymentOrderFailureAction,
  GeneratePaymentOrderSuccessAction,
  GetOrderAction,
  GetOrderFailureAction,
  GetOrderSuccessAction,
  RefreshTokenAction,
  RefreshTokenSuccessAction,
} from '../../../store/user/user.actions';
import { config } from '../../../../config/appConfig';
import { AsyncState } from '../../../store';
import { ASHER_SMALL_LOGO } from '../../../../assets/assetsBase64';
import {
  selectSubscriptionPlans,
  selectUserInfo,
} from '../../../store/user/user.reducer';
import { SnackBarService } from '../../../services/snackbar.service';
import { AuthService } from '../../../services/auth.service';
import { SessionService } from '../../../services/session.service';
import { Location } from '@angular/common';
import {
  getCurrencySymbol,
  getDiscountedPercentage,
  getDiscountedPrice,
  formatAmount,
} from '../../../_helpers/common.functions';

declare const Razorpay: any;

@Component({
  selector: 'app-payment',
  standalone: false,
  templateUrl: './order-payment.component.html',
  styleUrl: './order-payment.component.scss',
})
export class OrderPaymentComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private _componentSubscriptions: Subscription[] = [];
  private razorpayScriptId = 'razorpay-checkout-js';
  private paymentFailedBeforeDismiss: boolean = false;
  private lastPaymentError: string | null = null;
  private isPaymentSuccessful: boolean = false;
  private isSessionRefreshed: boolean = false;
  private fulfillmentPollRetries = 0;
  private readonly MAX_FULFILLMENT_POLL_RETRIES = 5;
  private readonly FULFILLMENT_POLL_INTERVAL_MS = 2000;
  getCurrSymbol = getCurrencySymbol;
  formatAmount = formatAmount;

  orderId: string | null = null;
  order: IOrderInfo | null = null;
  OrderStatusEnum = OrderStatus;
  isOrderRefreshed: boolean = false;
  paymentErrorMsg: string | null = null;
  paymentSuccessMsg: string | null = null;
  showSubscriptionAsDialog: boolean = false;

  statuses = [
    {
      enum: this.OrderStatusEnum.Draft,
      message: 'Your order is in draft mode. Complete payment to finalize.',
    },
    {
      enum: this.OrderStatusEnum.InProgress,
      message:
        'Order is being processed. Check status again or contact support.',
    },
    {
      enum: this.OrderStatusEnum.OnHold,
      message: 'Order on hold due to pending actions. Contact support.',
    },
    {
      enum: this.OrderStatusEnum.Completed,
      message: 'Order successfully completed. Thank you!',
    },
    {
      enum: this.OrderStatusEnum.Cancelled,
      message: 'Order cancelled. Contact support if this was a mistake.',
    },
    {
      enum: this.OrderStatusEnum.Returned,
      message: 'Order returned. Review details or contact support.',
    },
  ];

  generatePaymentOrderState: AsyncState = {
    status: 'NotFetched',
  };
  orderInfoState: AsyncState = {
    status: 'NotFetched',
  };

  // Subscription Plan
  subscriptionPlan: ISubscriptionPlanWithTaxAndDiscountInfo | null = null;
  subscriptionPlanState: AsyncState = {
    status: 'NotFetched',
  };

  // expose imported functions to template
  public getDiscountedPercentage = getDiscountedPercentage;
  public getDiscountedPrice = getDiscountedPrice;

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private actions$: Actions,
    private router: Router,
    private snackBarService: SnackBarService,
    private authService: AuthService,
    private sessionService: SessionService,
    private location: Location,
  ) {
    const nav = this.router.getCurrentNavigation();
    this.showSubscriptionAsDialog =
      nav?.extras?.state?.['showSubscriptionAsDialog'] ?? true;
  }

  onPreviousPageBtnClick() {
    if (
      !this.order ||
      (this.order && this.order.PaymentStatus == PaymentStatus.Paid)
    ) {
      this.router.navigate(['subscriptions'], {
        queryParams: {
          tab: 'billingHistory',
        },
      });
    } else {
      this.router.navigate(['subscriptions', 'create'], {
        queryParams: {
          subscriptionId: this.order?.SubscriptionPlanId,
        },
        state: {
          showSubscriptionAsDialog: this.showSubscriptionAsDialog,
        },
      });
    }
  }

  // Callback for successful payment
  private handlePaymentSuccess = (response: any) => {
    this.paymentErrorMsg = null;
    this.paymentFailedBeforeDismiss = false;
    this.lastPaymentError = null;
    this.paymentSuccessMsg =
      'Payment completed successfully! Order is being processed.';
    this.snackBarService.showSnackBar(
      'success',
      'Payment completed successfully!.',
    );
    this.isPaymentSuccessful = true;
    this.fulfillmentPollRetries = 0;
    this.store.dispatch(GetOrderAction({ orderId: this.orderId! }));
  };

  // Callback for payment failure
  private handlePaymentFailure = (response: any) => {
    const errorReason = response.error?.description || 'Unknown error occurred';
    this.lastPaymentError = `${errorReason} Please try again or contact support.`;
    this.paymentErrorMsg = this.lastPaymentError;
    this.paymentFailedBeforeDismiss = true;
    this.snackBarService.showSnackBar('error', 'Payment Failed');
    this.isPaymentSuccessful = false;
    this.store.dispatch(GetOrderAction({ orderId: this.orderId! }));
  };

  // Callback for when user closes the Razorpay dialog
  private handlePaymentDismiss = () => {
    if (this.paymentFailedBeforeDismiss && this.lastPaymentError) {
      this.paymentErrorMsg = this.lastPaymentError;
      this.snackBarService.showSnackBar('error', 'Payment Dismissed.');
    } else {
      this.paymentErrorMsg =
        'Payment was cancelled or dialog closed. Please try again to complete your order.';
      this.snackBarService.showSnackBar('error', 'Payment Dismissed.');
    }
    this.isPaymentSuccessful = false;
    this.store.dispatch(GetOrderAction({ orderId: this.orderId! }));
  };

  // Check for pending payment status
  private checkPendingPayment = () => {
    this.paymentErrorMsg = null;
    this.paymentFailedBeforeDismiss = false;
    this.lastPaymentError = null;
    this.snackBarService.showSnackBar(
      'warning',
      'Payment is pending. We are verifying with the bank. Please check status again soon.',
    );
    this.isPaymentSuccessful = false;
    this.store.dispatch(GetOrderAction({ orderId: this.orderId! }));
  };

  // Handle network or timeout issues
  private handleNetworkOrTimeoutError = () => {
    this.paymentErrorMsg =
      'Network issue detected. Please check your connection and try again.';
    this.snackBarService.showSnackBar('error', this.paymentErrorMsg);
  };

  // Load the RazorPay Script
  private loadRazorpayScript(): void {
    if (document.getElementById(this.razorpayScriptId)) {
      return; // script already loaded
    }

    const script = document.createElement('script');
    script.id = this.razorpayScriptId;
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }

  onRefreshStatusBtnClick() {
    if (this.orderId) {
      this.isOrderRefreshed = true;
      // In Case the Payment Is Paid and Order Status Is Still In Progress, Dispatch the Order Fulfill Action
      // It will automatically refresh the session, user token/subscription and order status
      if (
        this.order?.PaymentStatus === PaymentStatus.Paid &&
        this.order.Status === OrderStatus.InProgress
      ) {
        this.store.dispatch(
          FulFillOrderAction({
            request: {
              OrderId: this.orderId,
              SendEmail: true,
            },
          }),
        );
        return;
      }
      this.store.dispatch(GetOrderAction({ orderId: this.orderId }));
    }
  }

  getDiscount(data: ISubscriptionPlanInfo): { text: string } | null {
    // Check the Features array for DiscountPercent or DiscountAmount
    const discountFeature = data.Features.find(
      (feature) =>
        feature.FeatureType === IFeatureType.DiscountPercent ||
        feature.FeatureType === IFeatureType.DiscountAmount,
    );

    // If no discount feature is found, return null
    if (!discountFeature || !discountFeature.Value) {
      return null;
    }

    // Handle DiscountPercent
    if (discountFeature.FeatureType === IFeatureType.DiscountPercent) {
      return { text: `Save ${discountFeature.Value}%` };
    }

    // Handle DiscountAmount
    if (discountFeature.FeatureType === IFeatureType.DiscountAmount) {
      return {
        text: `Save ${getCurrencySymbol(data.Currency)} ${
          discountFeature.Value
        }`,
      };
    }

    return null;
  }

  showRefreshOrderStatusBtn(): boolean {
    return true;
  }

  showPaymentBtn(): boolean {
    return (
      this.order?.Status === OrderStatus.Draft ||
      this.order?.PaymentStatus === PaymentStatus.PaymentPending ||
      this.order?.PaymentStatus === PaymentStatus.PaymentFailed
    );
  }

  async onPayBtnClick() {
    if (!this.order) {
      return;
    }

    const isAutoRenew = this.subscriptionPlan?.IsAutoRenewing === true;

    // Auto-renew orders already created the RazorPay subscription at order-creation
    // time - PaymentOrderId here is the subscription id, so skip regenerating it.
    if (isAutoRenew && this.order.PaymentOrderId) {
      this.setupAndOpenRazorpay(this.order.PaymentOrderId, true);
      return;
    }

    this.store.dispatch(
      GeneratePaymentOrderAction({ orderId: this.order.OrderId }),
    );
    this.generatePaymentOrderState = {
      status: 'Loading',
    };
  }

  private async setupAndOpenRazorpay(
    paymentOrderId: string,
    isSubscription: boolean = false,
  ) {
    if (!this.order) {
      this.snackBarService.showSnackBar(
        'error',
        'No order found. Please try again or contact support.',
      );
      return;
    }

    try {
      const userInfo = await firstValueFrom(
        this.store.select(selectUserInfo).pipe(filter(Boolean)),
      );

      const options: any = {
        key: config.razorPayKey,
        amount: this.order.Amount,
        currency: this.subscriptionPlan?.Currency,
        name: config.appName,
        image: ASHER_SMALL_LOGO,
        handler: this.handlePaymentSuccess.bind(this),
        modal: {
          ondismiss: this.handlePaymentDismiss.bind(this),
        },
        prefill: {
          name: userInfo.Fullname,
          email: userInfo.Email,
          contact: userInfo.Mobile,
        },
        notes: {
          address: 'Razorpay Corporate Office',
          paymentSource: 'Website',
        },
        config: {
          display: {
            blocks: {
              paymentOptions: {
                name: 'Payment Options',
                instruments: [
                  {
                    method: 'netbanking',
                    banks: [
                      'AUBL', // AU Small Finance Bank
                      'AIRP', // Airtel Payments Bank
                      'ALLA', // Allahabad Bank
                      'BARB_R', // Bank of Baroda Retail Banking
                      'MAHB', // Bank of Maharashtra
                      'CNRB', // Canara Bank
                      'CSBK', // Catholic Syrian Bank
                      'CBIN', // Central Bank of India
                      'DCBL', // DCB Bank
                      'DEUT', // Deutsche Bank
                      'DLXB', // Dhanlaxmi Bank
                      'ESFB', // Equitas Small Finance Bank
                      'IBKL', // IDBI Bank
                      'IDFB', // IDFC FIRST Bank
                      'IDIB', // Indian Bank
                      'IOBA', // Indian Overseas Bank
                      'INDB', // Indusind Bank
                      'JAKA', // Jammu and Kashmir Bank
                      'JSFB', // Jana Small Finance Bank
                      'KARB', // Karnataka Bank
                      'KVBL', // Karur Vysya Bank
                      'ORBC', // PNB (Erstwhile-Oriental Bank of Commerce)
                      'UTBI', // PNB (Erstwhile-United Bank of India)
                      'PSIB', // Punjab & Sind Bank
                      'PUNB_R', // Punjab National Bank Retail Banking
                      'RATN', // RBL Bank (Ratnakar Bank)
                      'SRCB', // Saraswat Co-operative Bank
                      'SIBL', // South Indian Bank
                      'TMBL', // Tamilnadu Mercantile Bank
                      'UBIN', // Union Bank of India
                      'VIJB', // Vijaya Bank
                      'YESB', // Yes Bank
                    ],
                  },
                  {
                    method: 'upi',
                  },
                  {
                    method: 'card',
                  },
                ],
              },
            },
            sequence: ['block.paymentOptions'],
            preferences: {
              show_default_blocks: false,
            },
          },
        },
        theme: {
          color: '#3399cc',
        },
      };

      if (isSubscription) {
        options.subscription_id = paymentOrderId;
      } else {
        options.order_id = paymentOrderId;
      }

      const rzp1 = new Razorpay(options);
      rzp1.on('payment.failed', this.handlePaymentFailure.bind(this));
      rzp1.on('payment.pending', this.checkPendingPayment.bind(this));
      rzp1.open();
    } catch (error) {
      console.error('Razorpay Setup Error:', error);
      this.handleNetworkOrTimeoutError();
    }
  }

  ngOnInit(): void {
    this.paymentSuccessMsg = null;
    this.isPaymentSuccessful = false;
    // Load the RazorPay Script
    this.loadRazorpayScript();

    // Subscribe to Query Parameters
    this._componentSubscriptions.push(
      this.route.queryParamMap.subscribe((params) => {
        this.orderId = params.get('transactionId');
        if (this.orderId) {
          this.store.dispatch(GetOrderAction({ orderId: this.orderId }));
          this.orderInfoState = {
            status: 'Loading',
          };
        }
      }),
    );

    // Handle Get Order Success
    this._componentSubscriptions.push(
      this.actions$
        .pipe(ofType(GetOrderSuccessAction), takeUntil(this.destroy$))
        .subscribe(({ response }) => {
          this.order = response.Value;
          this.orderInfoState = {
            status: 'Success',
          };

          // When the Payment Is Successful
          if (this.isPaymentSuccessful) {
            if (response.Value.Status === OrderStatus.Completed) {
              // Already fulfilled server-side by the webhook - just refresh the token.
              this.isPaymentSuccessful = false;
              this.store.dispatch(
                RefreshTokenAction({
                  request: {
                    Token:
                      this.authService.getTokenInformation()?.RefreshToken!,
                    RefreshParentalControl: true,
                  },
                }),
              );
            } else if (response.Value.PaymentStatus === PaymentStatus.Paid) {
              this.isPaymentSuccessful = false;
              this.store.dispatch(
                FulFillOrderAction({
                  request: {
                    OrderId: response.Value.OrderId,
                    SendEmail: true,
                  },
                }),
              );
            } else if (
              this.fulfillmentPollRetries < this.MAX_FULFILLMENT_POLL_RETRIES
            ) {
              // Webhook (subscription.charged) likely hasn't landed yet - poll briefly.
              this.fulfillmentPollRetries++;
              setTimeout(() => {
                this.store.dispatch(GetOrderAction({ orderId: this.orderId! }));
              }, this.FULFILLMENT_POLL_INTERVAL_MS);
            } else {
              this.isPaymentSuccessful = false;
              this.snackBarService.showSnackBar(
                'warning',
                'Payment received, still confirming with the bank. Use "Refresh Status" in a moment.',
              );
            }
          }

          // When the Token And Session are both refreshed
          if (this.isSessionRefreshed) {
            this.isSessionRefreshed = false;
            setTimeout(() => {
              this.snackBarService.showSnackBar(
                'success',
                'Subscription Updated Successfully',
              );
            }, 500);
          }

          // Show Snackbar When User Pressed the "Refresh Order Status" button
          if (this.isOrderRefreshed) {
            this.isOrderRefreshed = false;
            this.snackBarService.showSnackBar(
              'success',
              'Status updated successfully!.',
            );
          }

          // fetch updated subscription plans, needed updated plans so that new orderId is updated in the plans when new order is created
          this.store.dispatch(FetchSubscriptionPlansAction());
          this.subscriptionPlanState = { status: 'Loading' };

          // Check Store For Subscription Plans
          this._componentSubscriptions.push(
            this.store
              .select(selectSubscriptionPlans)
              .subscribe((subscriptions) => {
                if (subscriptions.state.status === 'Success') {
                  // Plans exist, check for the subscription ID from the order
                  const foundPlan = subscriptions.plans.find(
                    (plan) => plan.Id === this.order?.SubscriptionPlanId,
                  );
                  if (foundPlan) {
                    this.subscriptionPlan = foundPlan;
                    this.subscriptionPlanState = { status: 'Success' };
                  } else {
                    this.subscriptionPlan = null;
                    this.subscriptionPlanState = { status: 'Failure' };
                  }
                } else if (subscriptions.state.status === 'NotFetched') {
                  // Plans not in store, dispatch action to fetch
                  this.store.dispatch(FetchSubscriptionPlansAction());
                  this.subscriptionPlanState = { status: 'Loading' };
                } else if (subscriptions.state.status === 'Loading') {
                  this.subscriptionPlanState = { status: 'Loading' };
                } else if (subscriptions.state.status === 'Failure') {
                  this.subscriptionPlan = null;
                  this.subscriptionPlanState = { status: 'Failure' };
                }
              }),
          );
        }),
    );

    // Handle Order Fulfilment Success
    this._componentSubscriptions.push(
      this.actions$
        .pipe(ofType(FulfillOrderSuccessAction), takeUntil(this.destroy$))
        .subscribe(({ response }) => {
          if (this.isOrderRefreshed) {
            this.isOrderRefreshed = false;
            this.snackBarService.showSnackBar(
              'success',
              'Status updated successfully!.',
            );
          }
          // Refresh The Token After Order Is Fulfilled
          this.store.dispatch(
            RefreshTokenAction({
              request: {
                Token: this.authService.getTokenInformation()?.RefreshToken!,
                RefreshParentalControl: true,
              },
            }),
          );
        }),
    );

    // Handle Refresh Token Success
    this._componentSubscriptions.push(
      this.actions$
        .pipe(ofType(RefreshTokenSuccessAction), takeUntil(this.destroy$))
        .subscribe(async ({ response }) => {
          this.isSessionRefreshed = true;
          // Again Initialize the Session Token When Token Is Refreshed
          await this.sessionService.initializeApp();
          // Fetch the Order Details To Update On the Page the last updated order
          setTimeout(() => {
            this.store.dispatch(GetOrderAction({ orderId: this.orderId! }));
          }, 1000);
        }),
    );

    // Handle Get Order Failure
    this._componentSubscriptions.push(
      this.actions$
        .pipe(ofType(GetOrderFailureAction), takeUntil(this.destroy$))
        .subscribe(({ error }) => {
          const isInvalidSession = error?.Errors?.some(
            (e: any) => e.Code === 'invalid-session-token',
          );

          if (isInvalidSession && this.isPaymentSuccessful) {
            // Session was likely revoked by a webhook-triggered fulfillment
            // (RevokeSessions was called). Refresh and retry instead of failing out.
            this.store.dispatch(
              RefreshTokenAction({
                request: {
                  Token: this.authService.getTokenInformation()?.RefreshToken!,
                  RefreshParentalControl: true,
                },
              }),
            );
            return;
          }

          this.orderInfoState = {
            status: 'Failure',
          };
        }),
    );

    // Handle Generate Payment Order Success
    this._componentSubscriptions.push(
      this.actions$
        .pipe(
          ofType(GeneratePaymentOrderSuccessAction),
          takeUntil(this.destroy$),
        )
        .subscribe(({ response }) => {
          this.generatePaymentOrderState = {
            status: 'Success',
          };
          // Assuming response contains the Razorpay order ID
          const paymentOrderId = response.Value?.PaymentOrderId;
          if (paymentOrderId) {
            const isAutoRenew = this.subscriptionPlan?.IsAutoRenewing === true;
            this.setupAndOpenRazorpay(paymentOrderId, isAutoRenew);
          } else {
            // If There Is No Payment Order Id, Mark It as Failure
            this.generatePaymentOrderState = {
              status: 'Failure',
              error:
                'Unable to generate payment order. Please retry to complete your order or contact support if the issue persists.',
            };
          }
        }),
    );

    // Handle Generate Payment Order Failure
    this._componentSubscriptions.push(
      this.actions$
        .pipe(
          ofType(GeneratePaymentOrderFailureAction),
          takeUntil(this.destroy$),
        )
        .subscribe(({ error }) => {
          this.generatePaymentOrderState = {
            status: 'Failure',
            error:
              'Unable to generate payment order. Please retry to complete your order or contact support if the issue persists.',
          };
        }),
    );
  }

  ngOnDestroy(): void {
    this._componentSubscriptions.forEach((sub) => sub.unsubscribe());

    // Remove the RazorPay Script
    const script = document.getElementById(this.razorpayScriptId);
    if (script) {
      script.remove();
    }
  }
}
